// Geometry utilities for spatial queries (point-in-zone testing)
// Algorithms adapted from ElevationService but working with zone editor coordinates

interface ZoneShape {
  x: number       // center X in editor coords
  y: number       // center Y in editor coords
  width: number   // in editor coords
  height: number  // in editor coords
  rotation: number // degrees
  shapeKind?: string
  points?: Array<{ x: number; y: number }>
}

/**
 * Test if point (px, py) is inside a zone shape (all in editor coords).
 * Dispatch:
 * - Has `points` array with ≥3 points → polygon (ray-casting)
 * - `shapeKind === 'ellipse'` → ellipse equation
 * - Default → rotated rectangle
 */
export function isPointInZone(px: number, py: number, zone: ZoneShape): boolean {
  if (zone.points && zone.points.length >= 3) {
    return isPointInPolygon(px, py, zone)
  }
  if (zone.shapeKind === 'ellipse') {
    return isPointInRotatedEllipse(px, py, zone)
  }
  return isPointInRotatedRectangle(px, py, zone)
}

/**
 * Check if point is inside a rotated rectangle.
 * Transform point into local (unrotated) coordinate space, then check bounds.
 */
function isPointInRotatedRectangle(px: number, py: number, zone: ZoneShape): boolean {
  const rad = -zone.rotation * Math.PI / 180
  const cos = Math.cos(rad)
  const sin = Math.sin(rad)
  const dx = px - zone.x
  const dy = py - zone.y
  const localX = dx * cos - dy * sin
  const localY = dx * sin + dy * cos

  return Math.abs(localX) <= zone.width / 2 && Math.abs(localY) <= zone.height / 2
}

/**
 * Check if point is inside a rotated ellipse.
 * Transform point into local space, then apply ellipse equation.
 */
function isPointInRotatedEllipse(px: number, py: number, zone: ZoneShape): boolean {
  const rad = -zone.rotation * Math.PI / 180
  const cos = Math.cos(rad)
  const sin = Math.sin(rad)
  const dx = px - zone.x
  const dy = py - zone.y
  const localX = dx * cos - dy * sin
  const localY = dx * sin + dy * cos

  const rx = zone.width / 2
  const ry = zone.height / 2
  if (rx <= 0 || ry <= 0) return false

  return (localX * localX) / (rx * rx) + (localY * localY) / (ry * ry) <= 1
}

/**
 * Check if point is inside a polygon using ray-casting algorithm.
 * Points are center-relative (same convention as ZoneState).
 */
function isPointInPolygon(px: number, py: number, zone: ZoneShape): boolean {
  if (!zone.points || zone.points.length < 3) return false

  // Convert point to local polygon coordinates (relative to center)
  const localX = px - zone.x
  const localY = py - zone.y

  // Ray-casting algorithm
  const points = zone.points
  const n = points.length
  let inside = false

  for (let i = 0, j = n - 1; i < n; j = i++) {
    const xi = points[i].x
    const yi = points[i].y
    const xj = points[j].x
    const yj = points[j].y

    const intersect = ((yi > localY) !== (yj > localY)) &&
      (localX < (xj - xi) * (localY - yi) / (yj - yi) + xi)

    if (intersect) inside = !inside
  }

  return inside
}

/** Calculate area of a zone in square meters (handles rect + polygon) */
export function calculateZoneArea(zone: ZoneShape, dpm: number): number {
  if (zone.points && zone.points.length >= 3) {
    // Shoelace formula on center-relative points
    const pts = zone.points
    const n = pts.length
    let area = 0
    for (let i = 0, j = n - 1; i < n; j = i++) {
      area += pts[j].x * pts[i].y - pts[i].x * pts[j].y
    }
    return Math.abs(area) / 2 / (dpm * dpm)
  }
  // Default: rectangle
  return (zone.width / dpm) * (zone.height / dpm)
}

/** Calculate minimum distance from point to zone boundary in meters */
export function distanceToZoneBoundary(px: number, py: number, zone: ZoneShape, dpm: number): number {
  const vertices = getZoneVertices(zone)
  const n = vertices.length
  let minDist = Infinity

  for (let i = 0, j = n - 1; i < n; j = i++) {
    const dist = pointToSegmentDistance(px, py, vertices[j].x, vertices[j].y, vertices[i].x, vertices[i].y)
    if (dist < minDist) minDist = dist
  }

  return minDist / dpm
}

/** Get zone vertices in absolute editor coordinates */
function getZoneVertices(zone: ZoneShape): Array<{ x: number; y: number }> {
  if (zone.points && zone.points.length >= 3) {
    return zone.points.map(p => ({ x: p.x + zone.x, y: p.y + zone.y }))
  }
  // Rectangle: 4 corners accounting for rotation
  const rad = zone.rotation * Math.PI / 180
  const cos = Math.cos(rad)
  const sin = Math.sin(rad)
  const hw = zone.width / 2
  const hh = zone.height / 2
  const corners = [
    { x: -hw, y: -hh },
    { x: hw, y: -hh },
    { x: hw, y: hh },
    { x: -hw, y: hh },
  ]
  return corners.map(c => ({
    x: zone.x + c.x * cos - c.y * sin,
    y: zone.y + c.x * sin + c.y * cos,
  }))
}

/** Distance from point to line segment */
function pointToSegmentDistance(px: number, py: number, x1: number, y1: number, x2: number, y2: number): number {
  const dx = x2 - x1
  const dy = y2 - y1
  const lenSq = dx * dx + dy * dy
  if (lenSq === 0) {
    // Degenerate segment (point)
    const ddx = px - x1
    const ddy = py - y1
    return Math.sqrt(ddx * ddx + ddy * ddy)
  }
  let t = ((px - x1) * dx + (py - y1) * dy) / lenSq
  t = Math.max(0, Math.min(1, t))
  const projX = x1 + t * dx
  const projY = y1 + t * dy
  const ex = px - projX
  const ey = py - projY
  return Math.sqrt(ex * ex + ey * ey)
}
